import { Link } from "react-router-dom";
import { Cloud, ShieldCheck, X } from "lucide-react";

// One-time consent shown the first time a student would use the SERVER
// fallback transcription path. Distinct from the native-only consent
// (``VoiceConsentCard``) because the privacy posture is materially
// different: audio actually leaves the device.
//
// Copy is deliberate — do NOT reword without owner sign-off:
//   * Discloses that audio is uploaded for transcription.
//   * States that SuperZeka will not intentionally retain the raw
//     recording, only the transcript proceeds to chat.
//   * Links /gizlilik for full detail.
//   * No vendor / model / engine name.
//
// Persistence:
//   sz_voice_server_consent_v1 = "granted" -> fallback enabled
//   sz_voice_server_consent_v1 = "denied"  -> fallback disabled;
//                                              user stays on typing.
export function VoiceServerConsentCard({ open, onAccept, onDecline }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="voice-server-consent-title"
      data-testid="voice-server-consent-card"
    >
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl border border-zinc-200 overflow-hidden">
        <div className="px-5 pt-5 pb-3 flex items-start gap-3">
          <div className="w-9 h-9 shrink-0 rounded-xl bg-[#1E3A8A]/5 border border-[#1E3A8A]/10 flex items-center justify-center">
            <Cloud className="w-4 h-4 text-[#1E3A8A]" aria-hidden />
          </div>
          <div className="flex-1 min-w-0">
            <h2
              id="voice-server-consent-title"
              className="text-base font-semibold text-zinc-900"
              data-testid="voice-server-consent-title"
            >
              Bu tarayıcı sesli girişi tek başına yapamıyor
            </h2>
            <p className="mt-1 text-xs text-zinc-600 leading-relaxed">
              Konuşmanın yazıya çevrilmesi için kısa süreliğine SuperZeka'ya
              gönderilmesi gerekir. Yalnızca sen izin verirsen açılır.
            </p>
          </div>
          <button
            type="button"
            onClick={onDecline}
            className="text-zinc-400 hover:text-zinc-700 -mt-1"
            aria-label="Kapat"
            data-testid="voice-server-consent-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 pb-4 space-y-3">
          <div
            className="flex gap-2 rounded-xl bg-zinc-50 border border-zinc-200 px-3 py-2.5 text-xs text-zinc-700 leading-relaxed"
            data-testid="voice-server-consent-body"
          >
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-600" aria-hidden />
            <div>
              Sesin yalnızca <strong>yazıya çevirmek için</strong> SuperZeka
              sunucusuna gönderilir. <strong>SuperZeka ham ses kaydını
              bilerek saklamaz</strong>; sohbete yalnızca çevrilen metin
              gider. Kayıt tek seferliktir, en fazla 30 saniye sürer.
              Daha fazlası için{" "}
              <Link
                to="/gizlilik"
                className="underline underline-offset-2 text-[#1E3A8A] hover:text-[#1E40AF]"
                data-testid="voice-server-consent-privacy-link"
              >
                Gizlilik Politikası
              </Link>
              .
            </div>
          </div>

          <ul className="text-[11.5px] text-zinc-600 space-y-1 pl-1">
            <li>• Mikrofon yalnızca butona bastığında ve tek seferliktir.</li>
            <li>• 30 saniye dolunca kayıt kendiliğinden durur.</li>
            <li>• Yazıya çevrilen metni gönderme kararı sana aittir.</li>
            <li>• Kabul etmezsen sesli giriş kapalı kalır; yazarak devam edebilirsin.</li>
          </ul>
        </div>

        <div className="px-5 py-4 border-t border-zinc-100 bg-zinc-50/60 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <button
            type="button"
            onClick={onDecline}
            className="h-9 px-4 rounded-full text-sm font-medium text-zinc-700 bg-white border border-zinc-200 hover:border-zinc-300"
            data-testid="voice-server-consent-decline"
          >
            Şimdi değil
          </button>
          <button
            type="button"
            onClick={onAccept}
            className="h-9 px-4 rounded-full text-sm font-medium text-white bg-[#1E3A8A] hover:bg-[#1E40AF]"
            data-testid="voice-server-consent-accept"
          >
            Kabul et ve sesli girişi aç
          </button>
        </div>
      </div>
    </div>
  );
}
